import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '@components/ui/Modal/Modal';
import Button from '@components/ui/Button/Button';
import TextInput from '@components/ui/Input/TextInput';
import Textarea from '@components/ui/Input/Textarea';
import { dsReviewService } from '@services/dsReviewService';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { ReviewTargetType } from '@workpay/ds-shared/types/review';
import styles from './SendForReviewModal.module.scss';

// ============================================
// TYPES
// ============================================

interface SendForReviewModalProps {
  targetType: ReviewTargetType;
  targetId: string;
  onClose: () => void;
  onSuccess?: () => void;
  initialReviewers?: Array<{ name: string; email: string }>;
}

interface ReviewerRow {
  id: string;
  name: string;
  email: string;
}

// ============================================
// HELPERS
// ============================================

function createEmptyReviewer(): ReviewerRow {
  return { id: crypto.randomUUID(), name: '', email: '' };
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ============================================
// COMPONENT
// ============================================

function SendForReviewModal({ targetType, targetId, onClose, onSuccess, initialReviewers }: SendForReviewModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [reviewers, setReviewers] = useState<ReviewerRow[]>(() => {
    if (initialReviewers && initialReviewers.length > 0) {
      return initialReviewers.map((r) => ({ id: crypto.randomUUID(), name: r.name, email: r.email }));
    }
    return [createEmptyReviewer()];
  });
  const [message, setMessage] = useState('');

  const handleAddReviewer = useCallback(() => {
    setReviewers((prev) => [...prev, createEmptyReviewer()]);
  }, []);

  const handleRemoveReviewer = useCallback((index: number) => {
    setReviewers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleReviewerChange = useCallback((index: number, field: keyof ReviewerRow, value: string) => {
    setReviewers((prev) =>
      prev.map((reviewer, i) => (i === index ? { ...reviewer, [field]: value } : reviewer)),
    );
  }, []);

  const hasValidReviewers = reviewers.some((r) => r.name.trim() && isValidEmail(r.email.trim()));

  const createMutation = useMutation({
    mutationFn: () => {
      const validReviewers = reviewers
        .filter((r) => r.name.trim() && isValidEmail(r.email.trim()))
        .map((r) => ({ name: r.name.trim(), email: r.email.trim() }));

      return dsReviewService.create({
        targetType,
        targetId,
        message: message.trim() || undefined,
        reviewers: validReviewers,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', targetType, targetId] });
      addNotification({
        variant: 'success',
        title: t('K_REVIEW_SENT_SUCCESS', 'Review request sent successfully.'),
      });
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const handleSubmit = useCallback(() => {
    if (!hasValidReviewers) {
      addNotification({
        variant: 'critical',
        title: t('K_REVIEW_VALIDATION_REVIEWERS', 'Please add at least one reviewer with a valid name and email.'),
      });
      return;
    }
    createMutation.mutate();
  }, [hasValidReviewers, createMutation, addNotification, t]);

  const footer = (
    <div className={styles.footerActions}>
      <Button variant="outline" onClick={onClose} disabled={createMutation.isPending}>
        {t('K_BUTTON_CANCEL', 'Cancel')}
      </Button>
      <Button
        variant="filled"
        onClick={handleSubmit}
        loading={createMutation.isPending}
        disabled={!hasValidReviewers}
      >
        {t('K_BUTTON_SEND_FOR_REVIEW', 'Send for Review')}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('K_MODAL_SEND_FOR_REVIEW', 'Send for Review')}
      size="md"
      footer={footer}
    >
      <div className={styles.form}>
        <div className={styles.reviewerList}>
          {reviewers.map((reviewer, index) => (
            <div key={reviewer.id} className={styles.reviewerRow}>
              <div className={styles.reviewerField}>
                <TextInput
                  label={index === 0 ? t('K_LABEL_REVIEWER_NAME', 'Reviewer Name') : undefined}
                  placeholder={t('K_PLACEHOLDER_REVIEWER_NAME', 'Enter name')}
                  value={reviewer.name}
                  onChange={(e) => handleReviewerChange(index, 'name', e.target.value)}
                  isRequired={index === 0}
                />
              </div>
              <div className={styles.reviewerField}>
                <TextInput
                  label={index === 0 ? t('K_LABEL_REVIEWER_EMAIL', 'Reviewer Email') : undefined}
                  placeholder={t('K_PLACEHOLDER_REVIEWER_EMAIL', 'Enter email')}
                  type="email"
                  value={reviewer.email}
                  onChange={(e) => handleReviewerChange(index, 'email', e.target.value)}
                  isRequired={index === 0}
                />
              </div>
              {reviewers.length > 1 && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => handleRemoveReviewer(index)}
                  aria-label={t('K_BUTTON_REMOVE_REVIEWER', 'Remove reviewer')}
                >
                  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12 4L4 12M4 4l8 8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className={styles.addReviewerButton}
          onClick={handleAddReviewer}
        >
          {t('K_BUTTON_ADD_REVIEWER', '+ Add Reviewer')}
        </Button>

        <Textarea
          label={t('K_LABEL_REVIEW_MESSAGE', 'Message (optional)')}
          placeholder={t('K_PLACEHOLDER_REVIEW_MESSAGE', 'Add a message for the reviewers...')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  );
}

export default SendForReviewModal;
